import {
  Component,
  computed,
  ElementRef,
  signal,
  viewChild,
  input,
  output,
  effect,
  untracked,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  ShamRatingStarIcon,
  ShamUsaFlagIcon,
  ShamSyFlagIcon,
} from 'sham-components/icons';
import { FormsModule } from '@angular/forms';
import {
  SiteCategoryBrowseSubCategory,
  SiteCategoryBrowseBrand,
  SiteCategoryBrowsePriceRange,
} from '@/app/services/content-api';
import { TranslateModule } from '@ngx-translate/core';

export interface CategoryFiltersChangeEvent {
  subCategories: string[];
  brands: string[];
  minPrice: number;
  maxPrice: number;
  ratings: number[];
  freeDelivery: boolean;
  newItems: boolean;
}

@Component({
  selector: 'app-category-filters',
  imports: [ShamRatingStarIcon, FormsModule, RouterLink, TranslateModule],
  templateUrl: './category-filters.html',
  styleUrl: './category-filters.css',
})
export class CategoryFilters {
  subCategories = input<SiteCategoryBrowseSubCategory[]>([]);
  categorySlug = input<string>('');
  activeSubSlug = input<string | null>(null);
  activeSub2Slug = input<string | null>(null);
  preservedQueryParams = input<Record<string, string | number | boolean | null>>({});
  activeFilters = input<CategoryFiltersChangeEvent>({
    subCategories: [],
    brands: [],
    minPrice: 0,
    maxPrice: 0,
    ratings: [],
    freeDelivery: false,
    newItems: false,
  });
  brands = input<SiteCategoryBrowseBrand[]>([]);
  priceRange = input<SiteCategoryBrowsePriceRange>({ minPrice: 0, maxPrice: 1000 });

  filtersChanged = output<CategoryFiltersChangeEvent>();

  readonly step = 5;

  isOpen = signal(true);
  minVal = signal(0);
  maxVal = signal(1000);

  selectedSubCategories = signal<Set<string>>(new Set());
  selectedBrands = signal<Set<string>>(new Set());
  selectedRatings = signal<Set<number>>(new Set());
  freeDelivery = signal(false);
  newItems = signal(false);

  viewMoreSubs = signal(false);
  viewMoreBrands = signal(false);

  trackContainer = viewChild<ElementRef<HTMLDivElement>>('trackContainer');
  private dragging: 'min' | 'max' | null = null;

  absMin = computed(() => this.priceRange().minPrice || 0);
  absMax = computed(() => this.priceRange().maxPrice || 1000);

  minPercent = computed(() => {
    const diff = this.absMax() - this.absMin();
    return diff === 0 ? 0 : ((this.minVal() - this.absMin()) / diff) * 100;
  });

  maxPercent = computed(() => {
    const diff = this.absMax() - this.absMin();
    return diff === 0 ? 100 : ((this.maxVal() - this.absMin()) / diff) * 100;
  });

  fillLeft = computed(() => this.minPercent());
  fillWidth = computed(() => this.maxPercent() - this.minPercent());

  constructor() {
    effect(() => {
      const range = this.priceRange();
      untracked(() => {
        this.minVal.set(range.minPrice || 0);
        this.maxVal.set(range.maxPrice || 1000);
      });
    });

    effect(() => {
      const subs = this.subCategories();
      const subSlug = this.activeSubSlug();
      const sub2Slug = this.activeSub2Slug();
      const selectedIds = this.resolveSelectedSubCategoryIds(subs, subSlug, sub2Slug);

      untracked(() => {
        this.selectedSubCategories.set(new Set(selectedIds));
      });
    });

    effect(() => {
      const filters = this.activeFilters();
      untracked(() => {
        this.selectedBrands.set(new Set(filters.brands));
        this.selectedRatings.set(new Set(filters.ratings));
        this.freeDelivery.set(filters.freeDelivery);
        this.newItems.set(filters.newItems);
        const min = Math.max(
          this.absMin(),
          Math.min(filters.minPrice, this.absMax()),
        );
        const max = Math.max(
          this.absMin(),
          Math.min(filters.maxPrice, this.absMax()),
        );
        this.minVal.set(Math.min(min, max - this.step));
        this.maxVal.set(Math.max(max, min + this.step));
      });
    });
  }

  emitChange() {
    this.filtersChanged.emit({
      subCategories: Array.from(this.selectedSubCategories()),
      brands: Array.from(this.selectedBrands()),
      minPrice: this.minVal(),
      maxPrice: this.maxVal(),
      ratings: Array.from(this.selectedRatings()),
      freeDelivery: this.freeDelivery(),
      newItems: this.newItems(),
    });
  }

  isFilteredBy(childId: string) {
    return this.selectedSubCategories().has(childId);
  }

  isParentFilteredBySlug(parentSlug: string) {
    return this.activeSubSlug() === parentSlug;
  }

  brandIsChecked(brandId: string) {
    return this.selectedBrands().has(brandId);
  }

  toggleBrandFilterOption(brandId: string) {
    this.selectedBrands.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(brandId)) newSet.delete(brandId);
      else newSet.add(brandId);
      return newSet;
    });
    this.emitChange();
  }

  ratingIsChecked(rating: number) {
    return this.selectedRatings().has(rating);
  }

  toggleRatingFilterOption(rating: number) {
    this.selectedRatings.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(rating)) newSet.delete(rating);
      else newSet.add(rating);
      return newSet;
    });
    this.emitChange();
  }

  freeDeliveryChecked() {
    return this.freeDelivery();
  }
  toggleFreeDeliveryFilter() {
    this.freeDelivery.update((v) => !v);
    this.emitChange();
  }

  newItemsChecked() {
    return this.newItems();
  }
  toggleNewItemsFilter() {
    this.newItems.update((v) => !v);
    this.emitChange();
  }

  toggleViewMoreSubs() {
    this.viewMoreSubs.update((v) => !v);
  }
  toggleViewMoreBrands() {
    this.viewMoreBrands.update((v) => !v);
  }

  toggleOpen() {
    this.isOpen.update((v) => !v);
  }

  startDrag(event: MouseEvent, handle: 'min' | 'max') {
    event.preventDefault();
    this.dragging = handle;

    const onMove = (e: MouseEvent) => this.onMouseMove(e);
    const onUp = () => {
      this.dragging = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      this.emitChange();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  onMouseMove(event: MouseEvent) {
    if (!this.dragging) return;
    const pct = this.getPercentFromEvent(event);
    const raw = this.absMin() + pct * (this.absMax() - this.absMin());
    const val = Math.round(raw / this.step) * this.step;

    if (this.dragging === 'min') {
      this.minVal.set(
        Math.min(Math.max(val, this.absMin()), this.maxVal() - this.step),
      );
    } else {
      this.maxVal.set(
        Math.max(Math.min(val, this.absMax()), this.minVal() + this.step),
      );
    }
  }

  onTrackMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      target === this.trackContainer()?.nativeElement ||
      target.classList.contains('rounded-full')
    ) {
      const pct = this.getPercentFromEvent(event);
      const raw = this.absMin() + pct * (this.absMax() - this.absMin());
      const val = Math.round(raw / this.step) * this.step;

      const midVal = (this.minVal() + this.maxVal()) / 2;
      if (val < midVal) {
        this.minVal.set(Math.min(val, this.maxVal() - this.step));
      } else {
        this.maxVal.set(Math.max(val, this.minVal() + this.step));
      }
      this.emitChange();
    }
  }

  onKeyDown(event: KeyboardEvent, handle: 'min' | 'max') {
    const delta = event.shiftKey ? this.step * 10 : this.step;

    if (handle === 'min') {
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.minVal.update((v) => Math.min(v + delta, this.maxVal() - this.step));
        this.emitChange();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.minVal.update((v) => Math.max(v - delta, this.absMin()));
        this.emitChange();
      }
    } else {
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.maxVal.update((v) => Math.min(v + delta, this.absMax()));
        this.emitChange();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.maxVal.update((v) => Math.max(v - delta, this.minVal() + this.step));
        this.emitChange();
      }
    }
  }

  private getPercentFromEvent(event: MouseEvent): number {
    const track = this.trackContainer()?.nativeElement;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
  }

  private resolveSelectedSubCategoryIds(
    items: SiteCategoryBrowseSubCategory[],
    subSlug: string | null,
    sub2Slug: string | null,
  ): string[] {
    if (!subSlug) return [];

    const parent = items.find((item) => item.slug === subSlug);
    if (!parent) return [];

    if (!sub2Slug) return [parent.id];

    const child = parent.children.find((item) => item.slug === sub2Slug);
    return child ? [child.id] : [parent.id];
  }
}
